import { parseSearchStringValue } from 'infrastructure/functions/parse-query-string';
import { useQueryFilterNew } from 'infrastructure/hooks/common/use-query-param-new';
import { usStates } from 'constants/states';

import type { TParseFunctions } from 'infrastructure/hooks/common/use-query-param-new';
import type { PodFilters, PodSortField } from '../types';

const validStateNames = new Set(usStates.map((s) => s.value));

type PodFilterState = Pick<
  PodFilters,
  'podType' | 'status' | 'states' | 'search' | 'sortBy' | 'sortOrder'
>;

const defaultFilter: PodFilterState = {
  podType: undefined,
  status: undefined,
  states: undefined,
  search: undefined,
  sortBy: undefined,
  sortOrder: undefined,
};

const podTypeValues = ['RPM', 'CCM'] as const;
const statusValues = ['active', 'inactive'] as const;

const parseEnumValue = <T extends string>(
  value: string | (string | null)[] | null,
  allowed: readonly T[],
): T | undefined => {
  const parsed = parseSearchStringValue<T>(value);
  return parsed && allowed.includes(parsed) ? parsed : undefined;
};

const parseStatesArray = (
  value: string | (string | null)[] | null,
): string[] | undefined => {
  if (!value) return undefined;
  const arr = (
    Array.isArray(value)
      ? (value.filter(Boolean) as string[])
      : value.split(',').map((s) => s.trim())
  ).filter((s) => validStateNames.has(s));
  return arr.length > 0 ? arr : undefined;
};

const sortByValues: readonly PodSortField[] = [
  'podName',
  'podType',
  'clinicCount',
  'patientCount',
  'memberCount',
  'status',
  'createdAt',
] as const;
const sortOrderValues = ['ASC', 'DESC'] as const;

const parseFunctions: TParseFunctions<PodFilterState> = {
  podType: (v) => parseEnumValue(v, podTypeValues),
  status: (v) => parseEnumValue(v, statusValues),
  states: parseStatesArray,
  search: (v) => {
    const parsed = parseSearchStringValue<string>(v);
    return parsed?.trim() || undefined;
  },
  sortBy: (v) => parseEnumValue(v, sortByValues),
  sortOrder: (v) => parseEnumValue(v, sortOrderValues),
};

export const usePodFilters = () =>
  useQueryFilterNew<PodFilterState>(defaultFilter, parseFunctions);
